const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Hero: rising embers over the background video.
 */
class SkzHero extends HTMLElement {
  /** @type {number | undefined} */
  #frame;
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    if (!reducedMotion) this.#setupEmbers();
  }

  disconnectedCallback() {
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#observer?.disconnect();
  }

  #setupEmbers() {
    const canvas = this.querySelector('.skz-hero__embers');
    if (!(canvas instanceof HTMLCanvasElement)) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const count = window.innerWidth < 750 ? 22 : 45;
    /** @type {{x:number,y:number,r:number,vy:number,vx:number,life:number,hue:number}[]} */
    let embers = [];
    let width = 0;
    let height = 0;
    let visible = true;

    const resize = () => {
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      width = this.clientWidth;
      height = this.clientHeight;
      canvas.width = width * ratio;
      canvas.height = height * ratio;
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    };

    const spawn = (initial = false) => ({
      x: Math.random() * width,
      y: initial ? Math.random() * height : height + 10,
      r: 0.8 + Math.random() * 2.2,
      vy: 0.3 + Math.random() * 0.9,
      vx: (Math.random() - 0.5) * 0.4,
      life: 0.4 + Math.random() * 0.6,
      hue: Math.random() < 0.7 ? 355 : 20,
    }); 

    resize();
    embers = Array.from({ length: count }, () => spawn(true));
    window.addEventListener('resize', resize);

    this.#observer = new IntersectionObserver(([entry]) => {
      visible = Boolean(entry?.isIntersecting);
      if (visible && !this.#frame) this.#frame = requestAnimationFrame(draw);
    });
    this.#observer.observe(this);

    const draw = () => {
      this.#frame = undefined;
      if (!visible) return;
      ctx.clearRect(0, 0, width, height);
      for (let i = 0; i < embers.length; i++) {
        const e = embers[i];
        if (!e) continue;
        e.y -= e.vy;
        e.x += e.vx + Math.sin(e.y / 40) * 0.3;
        if (e.y < -10) embers[i] = spawn();
        const alpha = Math.max(0, Math.min(1, e.y / height)) * e.life;
        ctx.beginPath();
        ctx.fillStyle = `hsla(${e.hue}, 90%, 55%, ${alpha})`;
        ctx.shadowColor = `hsla(${e.hue}, 90%, 50%, ${alpha})`;
        ctx.shadowBlur = 8;
        ctx.arc(e.x, e.y, e.r, 0, Math.PI * 2);
        ctx.fill();
      }
      this.#frame = requestAnimationFrame(draw);
    };
  }
}

/**
 * Flashlight: the lit copy of the photo is masked by a circle that follows the pointer;
 * without input the light drifts on its own.
 */
class SkzFlashlight extends HTMLElement {
  /** @type {number | undefined} */
  #frame;
  #lastInput = 0;
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    const frame = this.querySelector('.skz-flash__frame') || this;
    if (!(frame instanceof HTMLElement)) return;

    const place = (/** @type {number} */ clientX, /** @type {number} */ clientY) => {
      const rect = frame.getBoundingClientRect();
      this.style.setProperty('--x', `${clientX - rect.left}px`);
      this.style.setProperty('--y', `${clientY - rect.top}px`);
      this.#lastInput = performance.now();
    };

    this.addEventListener('pointermove', (event) => place(event.clientX, event.clientY));
    this.addEventListener('pointerdown', (event) => place(event.clientX, event.clientY));

    if (reducedMotion) return;

    let visible = false;
    const wander = (/** @type {number} */ time) => {
      this.#frame = undefined;
      if (!visible) return;
      if (time - this.#lastInput > 2500) {
        const rect = frame.getBoundingClientRect();
        const t = time / 1000;
        const x = rect.width * (0.5 + 0.32 * Math.sin(t * 0.7));
        const y = rect.height * (0.4 + 0.28 * Math.sin(t * 1.1 + 1));
        this.style.setProperty('--x', `${x}px`);
        this.style.setProperty('--y', `${y}px`);
      }
      this.#frame = requestAnimationFrame(wander);
    };

    this.#observer = new IntersectionObserver(([entry]) => {
      visible = Boolean(entry?.isIntersecting);
      if (visible && !this.#frame) this.#frame = requestAnimationFrame(wander);
    });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#observer?.disconnect();
  }
}

/**
 * Looks grid: category and color filters (the color filter repaints cards) and reveal on scroll.
 */
class SkzLooks extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;
  #category = 'all';
  /** @type {string | null} */
  #color = null;

  connectedCallback() {
    this.#setupReveal();

    this.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const cat = target?.closest('[data-filter-cat]');
      const color = target?.closest('[data-filter-color]');

      if (cat instanceof HTMLElement) {
        this.#category = cat.dataset.filterCat || 'all';
        this.#apply();
      } else if (color instanceof HTMLElement) {
        const value = color.dataset.filterColor || null;
        this.#color = this.#color === value ? null : value;
        this.#apply();
      }
    });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  #setupReveal() {
    if (reducedMotion || !('IntersectionObserver' in window)) return;
    this.classList.add('is-ready');
    this.#observer = new IntersectionObserver(
      (entries) => {
        let delay = 0;
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const el = /** @type {HTMLElement} */ (entry.target);
          el.style.transitionDelay = `${delay}ms`;
          el.classList.add('is-in');
          delay += 70;
          this.#observer?.unobserve(el);
        }
      },
      { rootMargin: '0px 0px -8% 0px' }
    );
    this.querySelectorAll('.skz-card, .skz-tile').forEach((el) => this.#observer?.observe(el));
  }

  /**
   * Shows a card's photo and link in the given tone (black, red or white).
   * @param {HTMLElement} card
   * @param {string} tone
   */
  #paint(card, tone) {
    const key = tone.charAt(0).toUpperCase() + tone.slice(1);
    const src = card.dataset[`img${key}`];
    const url = card.dataset[`url${key}`];
    if (!src || card.dataset.color === tone) return;
    card.dataset.color = tone;

    const link = card.querySelector('.skz-card__link');
    if (link instanceof HTMLAnchorElement && url) link.href = url;

    const img = card.querySelector('.skz-card__img');
    if (!(img instanceof HTMLImageElement)) return;
    const preload = new Image();
    preload.onload = () => {
      img.classList.add('is-swapping');
      setTimeout(() => {
        img.src = src;
        img.classList.remove('is-swapping');
      }, 180);
    };
    preload.src = src;
  }

  #apply() {
    this.querySelectorAll('[data-filter-cat]').forEach((chip) => {
      const active = chip instanceof HTMLElement && chip.dataset.filterCat === this.#category;
      chip.classList.toggle('is-active', active);
      chip.setAttribute('aria-pressed', String(active));
    });
    this.querySelectorAll('[data-filter-color]').forEach((chip) => {
      const active = chip instanceof HTMLElement && chip.dataset.filterColor === this.#color;
      chip.classList.toggle('is-active', active);
      chip.setAttribute('aria-pressed', String(active));
    });

    const filtering = this.#category !== 'all' || this.#color !== null;
    let shown = 0;

    this.querySelectorAll('.skz-card').forEach((card) => {
      if (!(card instanceof HTMLElement)) return;
      const colors = (card.dataset.colors || '').split(' ');
      const match =
        (this.#category === 'all' || card.dataset.cat === this.#category) &&
        (this.#color === null || colors.includes(this.#color));
      card.hidden = !match;
      if (!match) return;
      shown++;
      card.classList.add('is-in');
      const tone = this.#color || card.dataset.defaultColor;
      if (tone) this.#paint(card, tone);
    });

    this.querySelectorAll('.skz-tile').forEach((tile) => {
      if (tile instanceof HTMLElement) tile.hidden = filtering;
    });

    const empty = this.querySelector('.skz-looks__empty');
    if (empty instanceof HTMLElement) empty.hidden = shown > 0;
  }
}

/**
 * Trick or treat: "treat" spins through the looks on the page and lands on a random one,
 * "trick" plays a short jump scare.
 */
class SkzTrick extends HTMLElement {
  #busy = false;

  connectedCallback() {
    this.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-choice]') : null;
      if (!(button instanceof HTMLElement) || this.#busy) return;
      if (button.dataset.choice === 'treat') this.#treat();
      else this.#trick();
    });
  }

  #looks() {
    return [...document.querySelectorAll('.skz-card')]
      .map((card) => ({
        img: card.querySelector('.skz-card__img')?.getAttribute('src') || '',
        url: card.querySelector('.skz-card__link')?.getAttribute('href') || '',
        title: card.querySelector('.skz-card__title')?.textContent?.trim() || '',
      }))
      .filter((look) => look.img && look.url);
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;
    this.querySelectorAll('[data-choice]').forEach((button) => button.toggleAttribute('disabled', busy));
  }

  #treat() {
    const looks = this.#looks();
    const result = this.querySelector('.skz-trick__result');
    const img = this.querySelector('.skz-trick__img');
    const title = this.querySelector('.skz-trick__title');
    const shop = this.querySelector('.skz-trick__shop');
    const msg = this.querySelector('.skz-trick__msg');
    if (!looks.length || !(result instanceof HTMLElement) || !(img instanceof HTMLImageElement)) return;

    const pick = looks[Math.floor(Math.random() * looks.length)];
    if (!pick) return;
    if (msg) msg.textContent = '';
    result.hidden = false;
    this.#setBusy(true);
    this.classList.add('is-spinning');

    const finish = () => {
      img.src = pick.img;
      img.alt = pick.title;
      if (title) title.textContent = pick.title;
      if (shop instanceof HTMLAnchorElement) shop.href = pick.url;
      this.classList.remove('is-spinning');
      this.#setBusy(false);
    };

    if (reducedMotion) return finish();

    let step = 0;
    const steps = 18;
    const spin = () => {
      const look = looks[Math.floor(Math.random() * looks.length)];
      if (look) img.src = look.img;
      step++;
      if (step < steps) setTimeout(spin, 50 + step * step * 1.2);
      else finish();
    };
    spin();
  }

  #trick() {
    const result = this.querySelector('.skz-trick__result');
    const msg = this.querySelector('.skz-trick__msg');
    const gotcha = this.querySelector('template[data-gotcha]');
    if (result instanceof HTMLElement) result.hidden = true;
    if (msg) msg.textContent = '';

    this.#setBusy(true);
    this.classList.remove('is-scaring');
    void this.offsetWidth;
    this.classList.add('is-scaring');

    setTimeout(() => {
      this.classList.remove('is-scaring');
      if (msg && gotcha instanceof HTMLTemplateElement) msg.textContent = gotcha.content.textContent?.trim() || '';
      this.#setBusy(false);
    }, 1350);
  }
}

if (!customElements.get('skz-hero')) customElements.define('skz-hero', SkzHero);
if (!customElements.get('skz-flashlight')) customElements.define('skz-flashlight', SkzFlashlight);
if (!customElements.get('skz-looks')) customElements.define('skz-looks', SkzLooks);
if (!customElements.get('skz-trick')) customElements.define('skz-trick', SkzTrick);
